import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { currentLocaleTag } from '../lib/formatting'
import {
  Briefcase,
  Loader2,
  AlertCircle,
  Building2,
  CalendarRange,
  ChevronRight,
} from 'lucide-react'
import { api } from '../lib/api'
import { SELECTOR_PAGE_SIZE } from '../lib/constants'
import { useServiceLines } from '../lib/service-lines'
import {
  ExecutionPhaseLabel,
  RoadmapItemStatusLabel,
  type ExecutionPhase,
  type RoadmapItemStatus,
  type ClientListResponse,
} from '@toga/shared'

// ---------------------------------------------------------------------------
// Tipos del backend (apps/api/src/routes/projects.ts). No están en @toga/shared
// porque el endpoint es de solo lectura específico de esta pantalla.
// ---------------------------------------------------------------------------
interface ProjectListItem {
  id: string
  code: string
  title: string
  clientId: string
  clientName: string
  serviceLine: string
  executionPhase: ExecutionPhase
  openedAt: string
  roadmapItemsCount: number
  nextMilestone: { title: string; plannedEnd: string } | null
}

interface ProjectsListResponse {
  items: ProjectListItem[]
}

interface RoadmapOverviewItem {
  opportunityId: string
  opportunityCode: string
  title: string
  status: RoadmapItemStatus
  phase: ExecutionPhase | null
}

interface RoadmapOverviewMonth {
  month: number
  items: RoadmapOverviewItem[]
}

interface RoadmapOverviewResponse {
  year: number
  months: RoadmapOverviewMonth[]
}

const PHASE_BADGE: Record<ExecutionPhase, string> = {
  ONBOARDING: 'bg-amber-500/15 text-amber-700 dark:text-amber-200 border-amber-500/40',
  EJECUCION: 'bg-toga-500/15 text-toga-700 dark:text-toga-200 border-toga-500/30',
  FINALIZADO: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
  SOPORTE: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/40',
}

const STATUS_DOT: Record<RoadmapItemStatus, string> = {
  PENDIENTE: 'bg-slate-400',
  EN_CURSO: 'bg-toga-400',
  COMPLETADO: 'bg-emerald-400',
  BLOQUEADO: 'bg-rose-400',
}

const STATUS_BG: Record<RoadmapItemStatus, string> = {
  PENDIENTE: 'bg-slate-500/15 border-slate-500/30 text-slate-700 dark:text-slate-200',
  EN_CURSO: 'bg-toga-500/20 border-toga-500/40 text-toga-800 dark:text-toga-100',
  COMPLETADO: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-800 dark:text-emerald-100',
  BLOQUEADO: 'bg-rose-500/20 border-rose-500/40 text-rose-800 dark:text-rose-100',
}

function monthShortLabel(monthNumber: number): string {
  // monthNumber es 1..12; el año no afecta al nombre corto del mes.
  return new Date(2000, monthNumber - 1, 1).toLocaleDateString(currentLocaleTag(), {
    month: 'short',
  })
}

function formatShortDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(currentLocaleTag(), { day: '2-digit', month: 'short' })
}

export default function Projects() {
  const { t } = useTranslation('projects')
  const { lines, nameOf } = useServiceLines()
  const [searchParams, setSearchParams] = useSearchParams()

  const now = new Date()
  const currentYear = now.getUTCFullYear()
  // Por defecto NO filtramos por año en la tabla: 'Proyectos' es el menú de
  // proyectos VIVOS, y un proyecto plurianual (abierto en un año, aún en curso
  // en otro) desaparecía al filtrar por openedAt del año actual. '' = Todos.
  const year = searchParams.get('year') ?? ''
  // El roadmap anual SÍ necesita un año concreto: si el filtro está en 'Todos',
  // usamos el año en curso como referencia del timeline.
  const roadmapYear = year || String(currentYear)
  const clientId = searchParams.get('clientId') ?? ''
  const serviceLine = searchParams.get('serviceLine') ?? ''
  const phase = searchParams.get('phase') ?? ''

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    setSearchParams(next, { replace: true })
  }

  const [list, setList] = useState<ProjectsListResponse | null>(null)
  const [overview, setOverview] = useState<RoadmapOverviewResponse | null>(null)
  const [clients, setClients] = useState<Array<{ id: string; displayName: string }>>([])
  const [loadingList, setLoadingList] = useState(false)
  const [loadingOverview, setLoadingOverview] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Carga única del desplegable de clientes (mismo patrón que Quotes.tsx).
  useEffect(() => {
    api
      .get<ClientListResponse>(`/clients?pageSize=${SELECTOR_PAGE_SIZE}&sortBy=name&sortDir=asc`)
      .then((r) => setClients(r.items.map((c) => ({ id: c.id, displayName: c.displayName }))))
      .catch(() => setClients([]))
  }, [])

  const loadList = useCallback(async () => {
    setLoadingList(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (year) params.set('year', year)
      if (clientId) params.set('clientId', clientId)
      if (serviceLine) params.set('serviceLine', serviceLine)
      if (phase) params.set('phase', phase)
      const resp = await api.get<ProjectsListResponse>(`/projects?${params.toString()}`)
      setList(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.loadFailed'))
      setList(null)
    } finally {
      setLoadingList(false)
    }
  }, [year, clientId, serviceLine, phase])

  const loadOverview = useCallback(async () => {
    setLoadingOverview(true)
    try {
      const params = new URLSearchParams()
      // El roadmap siempre pide un año (nunca 'Todos').
      params.set('year', roadmapYear)
      const resp = await api.get<RoadmapOverviewResponse>(
        `/projects/roadmap-overview?${params.toString()}`
      )
      setOverview(resp)
    } catch {
      setOverview(null)
    } finally {
      setLoadingOverview(false)
    }
  }, [roadmapYear])

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])

  const years = useMemo(() => {
    const ys: string[] = []
    const current = now.getUTCFullYear()
    for (let y = current + 1; y >= 2024; y--) ys.push(String(y))
    return ys
  }, [now])

  const maxItemsInMonth = useMemo(() => {
    if (!overview) return 0
    return overview.months.reduce((m, x) => Math.max(m, x.items.length), 0)
  }, [overview])

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-serif font-semibold t-primary flex items-center gap-2">
            <Briefcase size={22} className="text-emerald-700 dark:text-emerald-300" />
            {t('title')}
          </h1>
          <p className="t-muted text-sm mt-1">
            {t('subtitle')}
          </p>
        </div>
      </header>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={year}
          onChange={(e) => setParam('year', e.target.value)}
          className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
          aria-label={t('filters.year')}
        >
          <option value="">{t('filters.allYears')}</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select
          value={clientId}
          onChange={(e) => setParam('clientId', e.target.value)}
          className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
          aria-label={t('filters.client')}
        >
          <option value="">{t('filters.allClients')}</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.displayName}
            </option>
          ))}
        </select>
        <select
          value={serviceLine}
          onChange={(e) => setParam('serviceLine', e.target.value)}
          className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
          aria-label={t('filters.serviceLine')}
        >
          <option value="">{t('filters.allServiceLines')}</option>
          {lines.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
        <select
          value={phase}
          onChange={(e) => setParam('phase', e.target.value)}
          className="px-3 py-2 rounded-full bg-[var(--bg-input)] border border-[var(--border)] t-primary"
          aria-label={t('filters.executionPhase')}
        >
          <option value="">{t('filters.inProgress')}</option>
          {Object.keys(ExecutionPhaseLabel).map((k) => (
            <option key={k} value={k}>
              {t('enums:ExecutionPhase.' + k)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Tabla de proyectos */}
      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              <th className="px-4 py-3 font-medium">{t('table.code')}</th>
              <th className="px-4 py-3 font-medium">{t('table.project')}</th>
              <th className="px-4 py-3 font-medium">{t('table.client')}</th>
              <th className="px-4 py-3 font-medium">{t('table.line')}</th>
              <th className="px-4 py-3 font-medium">{t('table.phase')}</th>
              <th className="px-4 py-3 font-medium">{t('table.nextMilestone')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('table.milestones')}</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loadingList && !list ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> {t('loading')}
                </td>
              </tr>
            ) : list && list.items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center t-muted">
                  {t('empty')}
                </td>
              </tr>
            ) : (
              list?.items.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-base/50 last:border-b-0 hover:bg-hover/50"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/projects/${p.id}`}
                      className="font-mono text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
                    >
                      {p.code}
                    </Link>
                  </td>
                  <td className="px-4 py-3 t-primary">
                    <Link
                      to={`/projects/${p.id}`}
                      className="font-medium hover:underline flex items-center gap-2"
                    >
                      <Briefcase size={14} className="text-emerald-700 dark:text-emerald-300" />
                      {p.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 t-secondary text-xs">
                    <span className="inline-flex items-center gap-1">
                      <Building2 size={12} className="t-faint" />
                      {p.clientName}
                    </span>
                  </td>
                  <td className="px-4 py-3 t-secondary text-xs">
                    {nameOf(p.serviceLine)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${PHASE_BADGE[p.executionPhase]}`}
                    >
                      {t('enums:ExecutionPhase.' + p.executionPhase)}
                    </span>
                  </td>
                  <td className="px-4 py-3 t-secondary text-xs">
                    {p.nextMilestone ? (
                      <span className="inline-flex items-center gap-1">
                        <CalendarRange size={12} className="t-faint" />
                        <span className="truncate max-w-[180px]" title={p.nextMilestone.title}>
                          {p.nextMilestone.title}
                        </span>
                        <span className="t-faint">
                          · {formatShortDate(p.nextMilestone.plannedEnd)}
                        </span>
                      </span>
                    ) : (
                      <span className="t-faint">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right t-muted text-xs">
                    {p.roadmapItemsCount}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/projects/${p.id}`}
                      className="inline-flex items-center text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200"
                      aria-label={t('openProject')}
                    >
                      <ChevronRight size={16} />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Roadmap anual */}
      <section className="surface p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-serif font-semibold t-primary flex items-center gap-2">
            <CalendarRange size={18} className="text-emerald-700 dark:text-emerald-300" />
            {t('roadmap.title', { year: roadmapYear })}
          </h2>
          {loadingOverview && (
            <Loader2 size={14} className="animate-spin t-faint" />
          )}
        </div>

        {/* Leyenda */}
        <div className="flex flex-wrap items-center gap-3 text-xs t-muted mb-3">
          {(Object.keys(RoadmapItemStatusLabel) as RoadmapItemStatus[]).map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[s]}`} />
              {t('enums:RoadmapItemStatus.' + s)}
            </span>
          ))}
        </div>

        {/* Timeline 12 columnas */}
        <div className="overflow-x-auto">
          <div className="grid grid-cols-12 gap-2 min-w-[840px]">
            {(overview?.months ?? Array.from({ length: 12 }, (_, i) => ({ month: i + 1, items: [] as RoadmapOverviewItem[] }))).map((m) => (
              <div
                key={m.month}
                className="flex flex-col gap-1 rounded-lg bg-[var(--bg-input-soft)] border border-base/60 p-2"
                style={{ minHeight: Math.max(120, 48 + maxItemsInMonth * 36) }}
              >
                <div className="text-[11px] font-semibold tracking-wider t-faint uppercase mb-1">
                  {monthShortLabel(m.month)}
                </div>
                <div className="flex flex-col gap-1.5">
                  {m.items.length === 0 ? (
                    <span className="text-[11px] t-faint italic">—</span>
                  ) : (
                    m.items.map((it, idx) => (
                      <Link
                        key={`${it.opportunityId}-${idx}`}
                        to={`/opportunities/${it.opportunityId}`}
                        className={`block text-[11px] leading-snug rounded-md border px-1.5 py-1 hover:opacity-90 ${STATUS_BG[it.status]}`}
                        title={`${it.opportunityCode} · ${it.title} · ${t('enums:RoadmapItemStatus.' + it.status)}`}
                      >
                        <div className="font-mono text-[10px] opacity-80">
                          {it.opportunityCode}
                        </div>
                        <div className="truncate">{it.title}</div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
